import React from 'react';
import { Box, Button, ButtonBase, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Select from 'react-select';
import { scribeTokens } from './tokens';

const asSxArray = sx => Array.isArray(sx) ? sx : sx ? [sx] : [];
const useTokens = () => useTheme().scribe || scribeTokens;
const semanticElement = { page: 'h1', heading: 'h2', section: 'h3', body: 'p', label: 'span', helper: 'p', content: 'div' };

export function ScribeText({ kind = 'body', sx, component, color, ...props }) {
  const tokens = useTokens();
  return <Typography {...props} component={component || semanticElement[kind]} sx={[
    { ...tokens.typography[kind], fontFamily: tokens.fontFamily, color: color || tokens.colors.text }, ...asSxArray(sx),
  ]} />;
}

export const ScribePanel = React.forwardRef(function ScribePanel({ sx, ...props }, ref) {
  const tokens = useTokens();
  return <Box {...props} ref={ref} sx={[{ fontFamily: tokens.fontFamily, ...tokens.typography.body, color: tokens.colors.text, bgcolor: tokens.colors.surface, border: `1px solid ${tokens.colors.border}`, borderRadius: `${tokens.radius.panel}px`, overflow: 'hidden', minWidth: 0 }, ...asSxArray(sx)]} />;
});

export function ScribeButton({ tone = 'brand', variant = 'text', sx, ...props }) {
  const tokens = useTokens();
  const colors = tokens.colors;
  const main = tone === 'accent' ? colors.accent : tone === 'danger' ? colors.danger : colors.brand;
  const hover = tone === 'accent' ? colors.accentHover : tone === 'danger' ? colors.dangerHover : colors.brandHover;
  const soft = tone === 'accent' ? colors.accentSoft : tone === 'danger' ? colors.dangerSoft : colors.brandSoft;
  return <Button {...props} variant={variant} disableElevation sx={[
    { ...tokens.typography.label, fontFamily: tokens.fontFamily, textTransform: 'none', borderRadius: `${tokens.radius.control}px`,
      minHeight: props.size === 'small' ? tokens.controlHeight.compact : tokens.controlHeight.normal,
      px: props.size === 'small' ? 1.5 : 2, py: props.size === 'small' ? .5 : 1, whiteSpace: 'nowrap',
      color: variant === 'contained' ? colors.surface : main,
      bgcolor: variant === 'contained' ? main : 'transparent', borderColor: main,
      '&:hover': { bgcolor: variant === 'contained' ? hover : soft, borderColor: hover },
      '&.Mui-disabled': { color: colors.disabledText, bgcolor: variant === 'contained' ? colors.disabled : 'transparent', borderColor: colors.border },
    }, ...asSxArray(sx),
  ]} />;
}

export function ScribePanelHeader({ title, badge, actions, expanded, onToggle, disabled = false, sx }) {
  const tokens = useTokens();
  const titleContent = <><ScribeText kind="section" component="span">{title}</ScribeText>{badge}</>;
  return <Box sx={[{ display: 'flex', alignItems: 'center', gap: 1, p: 2, minHeight: 56 }, ...asSxArray(sx)]}>
    {onToggle ? <ButtonBase disabled={disabled} onClick={onToggle} aria-label={title} aria-expanded={expanded}
      sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1, flex: 1, minWidth: 0, minHeight: tokens.controlHeight.compact, borderRadius: `${tokens.radius.control}px`, '&.Mui-focusVisible': { outline: `2px solid ${tokens.colors.brand}`, outlineOffset: 2 } }}>
      {titleContent}<Box sx={{ flex: 1 }} />{expanded ? <KeyboardArrowDownIcon sx={{ color: tokens.colors.secondary }} /> : <KeyboardArrowRightIcon sx={{ color: tokens.colors.secondary }} />}
    </ButtonBase> : <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1 }}>{titleContent}</Box>}
    {actions}
  </Box>;
}

export function ScribeStepLabel({ number, children }) {
  const tokens = useTokens();
  return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
    <Box aria-hidden sx={{ width: 28, height: 28, flexShrink: 0, borderRadius: '50%', bgcolor: tokens.colors.brandSoft, color: tokens.colors.brand, display: 'grid', placeItems: 'center', ...tokens.typography.label }}>{number}</Box>
    <ScribeText kind="section">{children}</ScribeText>
  </Box>;
}

export function ScribeSelectField({ label, inputId, styles, ...props }) {
  const tokens = useTokens();
  return <Box>
    <ScribeText kind="label" component="label" htmlFor={inputId} sx={{ display: 'block', mb: .5, color: tokens.colors.secondary }}>{label}</ScribeText>
    <Select {...props} inputId={inputId} styles={{
      container: base => ({ ...base, width: '100%', fontFamily: tokens.fontFamily }),
      control: (base, state) => ({ ...base, ...tokens.typography.body, minHeight: tokens.controlHeight.normal, borderRadius: tokens.radius.control,
        backgroundColor: state.isDisabled ? tokens.colors.subtle : tokens.colors.surface,
        borderColor: state.isFocused ? tokens.colors.brand : tokens.colors.border,
        boxShadow: state.isFocused ? `0 0 0 1px ${tokens.colors.brand}` : 'none', '&:hover': { borderColor: tokens.colors.brand } }),
      option: (base, state) => ({ ...base, ...tokens.typography.body, color: tokens.colors.text,
        backgroundColor: state.isSelected || state.isFocused ? tokens.colors.brandSoft : tokens.colors.surface }),
      singleValue: base => ({ ...base, ...tokens.typography.body, color: tokens.colors.text }),
      menu: base => ({ ...base, zIndex: 20, borderRadius: tokens.radius.control }),
      ...styles,
    }} />
  </Box>;
}
